import React, {
	createContext,
	useCallback,
	useContext,
	useImperativeHandle,
	useMemo,
	useRef,
} from 'react';
import {
	type IContextChildren,
	type IContextRef,
	renderChildrenOrChildrenFunction,
} from '@netx/core-next';
import {useActionMenu, type UseActionMenuParams} from '../hooks/useActionMenu';

export type ActionMenuContextType = ReturnType<typeof useActionMenu>;

export const ActionMenuContext = createContext<
	ActionMenuContextType | undefined
>(undefined);

export const useActionMenuProvider = () => {
	const context = useContext(ActionMenuContext);
	if (context === undefined) {
		throw new Error(
			'`useActionMenuProvider` must be used within a `ActionMenuProvider`',
		);
	}
	return context;
};

export type ActionMenuProviderRef = IContextRef<ActionMenuContextType>;

export type ActionMenuProviderProps = UseActionMenuParams & {
	ref?: ActionMenuProviderRef;
	children: IContextChildren<ActionMenuContextType>;
};

const ActionMenuProviderInner = (
	{children, ...props}: ActionMenuProviderProps,
	ref: ActionMenuProviderRef,
) => {
	const actionMenu = useActionMenu(props);

	useImperativeHandle(ref, () => actionMenu, [actionMenu]);

	return (
		<ActionMenuContext.Provider value={actionMenu}>
			{renderChildrenOrChildrenFunction(children, actionMenu)}
		</ActionMenuContext.Provider>
	);
};

export const ActionMenuProviderPrimitive = React.forwardRef<
	ActionMenuContextType,
	ActionMenuProviderProps
>(ActionMenuProviderInner);

/**
 * ActionMenu context provider
 * @example
 * <ActionMenuProvider ref={actionMenuRef}>
 * 	...
 * </ActionMenuProvider>
 * @example
 * <ActionMenuProvider ref={actionMenuRef}>
 * 	{(actionMenu) => (
 * 		...
 * 	)}
 * </ActionMenuProvider>
 */
export const ActionMenuProvider = ActionMenuProviderPrimitive;

export default ActionMenuProvider;
